/**
 * @voltxampere/components — Operational amplifier (single-pole macro)
 *
 * Sprint 7 promotes the Sprint 4 placeholder `opamp` type into a
 * production-ready macro model:
 *
 *   - open-loop DC gain A0 (default 1e5 — 100 dB, generic
 *     741-class);
 *   - single pole at fp (default 10 Hz — again 741-ish);
 *   - soft output clipping via `tanh(A0·V_diff / Vsat)` with
 *     symmetric ±Vsat rails (default ±12 V — generic ±15 V supply
 *     minus a couple of V of headroom).
 *
 * DC behaviour is dominated by the high-gain VCVS approximation
 * with smooth saturation; transient adds a single internal pole
 * via a virtual capacitor companion so the classic first-order
 * step response falls out of the same stamp call.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const OpampParamsSchema = z
    .object({
    /** Open-loop DC gain (V/V). Default 1e5 (100 dB). */
    openLoopGain: z.number().positive().finite().default(1e5),
    /** Dominant pole frequency (Hz). Default 10 Hz — 741-class. */
    poleFrequency: z.number().positive().finite().default(10),
    /** Symmetric output saturation rails |±Vsat| (V). Default 12 V. */
    saturationVoltage: z.number().positive().finite().default(12),
    /**
     * Sprint 22 D1 / D-22-4 — gain-bandwidth product (Hz). Optional;
     * when present the AC stamp uses `pole = GBW / openLoopGain`
     * instead of `poleFrequency`, which is the more familiar
     * datasheet figure (e.g. µA741 ≈ 1 MHz, LM358 ≈ 1 MHz, TL072 ≈
     * 3 MHz). The two parameters are mathematically redundant —
     * authors should set whichever they have on hand. When both are
     * present GBW wins.
     */
    gainBandwidthProduct: z.number().positive().finite().optional(),
})
    .strict();
export const opampDefinition = {
    type: "opamp",
    category: "active",
    displayName: { tr: "Op-Amp", en: "Op-Amp" },
    symbol: "U",
    pins: [
        { id: "in_pos", side: "left", offset: 0, label: "+" },
        { id: "in_neg", side: "left", offset: 1, label: "-" },
        { id: "out", side: "right", offset: 0, label: "OUT" },
    ],
    parameterSchema: OpampParamsSchema,
    nonlinear: true,
};
//# sourceMappingURL=opamp.js.map